import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import Lottie from "react-lottie";
import {
  KnowledgeTrainingContext,
  KnowledgeTrainingContextValue,
} from "./KnowledgeTrainingContext";
import { CXKnowledgeTrainingPopup } from "../../components/CXKnowledgeTrainingPopup";
import { useAgentKnowledge } from "../../hooks/useAgentKnowledge";
import { useSubscriptionV2 } from "../SubscriptionV2Provider";
import ghostImage from "./assets/ghost.png";
import headAnimation from "./assets/head.json";

export const KnowledgeTrainingProvider: FC<
  PropsWithChildren<{ entityId: string }>
> = ({ entityId, children }) => {
  const { aiWorkflowsApi } = useRestApiProvider();
  const { t } = useTranslations();
  const { isCx } = useAppVariant();
  const {
    handleRefreshKnowledge,
    knowledgeInfoRefetch,
    untrainedKnowledgeIds,
    knowledgeInfo,
    hasAnyKnowledge,
  } = useAgentKnowledge(entityId);
  const { defaultAgentKnowledge } = useSubscriptionV2();
  const trainingRunInProgressRef = useRef(false);
  // Captured at the moment training completes (before knowledgeInfo is
  // refetched), so the completion popup wording stays stable regardless of
  // later re-renders.
  const [wasFirstTraining, setWasFirstTraining] = useState(false);

  const [trainAgent, { isLoading: isTrainingLoading }] =
    aiWorkflowsApi.useTrainAgentMutation();

  const handleTrainAgent = useCallback(
    async (untrainedKnowledge: string[]) => {
      try {
        setIsAgentRetrainedPopupOpen(false);
        await trainAgent({
          agentId: entityId,
          entityId,
          src_id: untrainedKnowledge,
        }).unwrap();
        return true;
      } catch {
        return false;
      }
    },
    [entityId, trainAgent]
  );

  const [isAgentRetrainedPopupOpen, setIsAgentRetrainedPopupOpen] =
    useState(false);
  const [isKnowledgeLimitPopupOpen, setIsKnowledgeLimitPopupOpen] =
    useState(false);
  const [trainingStatusPollingInterval, setTrainingStatusPollingInterval] =
    useState<number>();
  const {
    data: trainingStatus,
    isLoading: isTrainingStatusLoading,
    isError: isTrainingStatusError,
    isFetching: isTrainingStatusFetching,
    refetch: trainingStatusRefetch,
  } = aiWorkflowsApi.useGetTrainingStatusQuery(
    {
      agentId: entityId,
      entityId,
      meta: { silent: true },
    },
    {
      skip: !isCx,
      refetchOnMountOrArgChange: true,
      pollingInterval: trainingStatusPollingInterval,
    }
  );

  useEffect(() => {
    setTrainingStatusPollingInterval(
      trainingStatus?.status === "in_process" ? 20000 : undefined
    );
  }, [trainingStatus?.status]);

  useEffect(() => {
    setIsAgentRetrainedPopupOpen(false);
    setIsKnowledgeLimitPopupOpen(false);
    trainingRunInProgressRef.current = false;
    handleRefreshKnowledge();
    knowledgeInfoRefetch();
  }, [entityId, handleRefreshKnowledge, knowledgeInfoRefetch]);

  useEffect(() => {
    if (trainingStatus?.status === "in_process") {
      trainingRunInProgressRef.current = true;
    }
  }, [trainingStatus]);

  const isTotalSizeExceeded = useMemo(
    () =>
      (knowledgeInfo?.info.total_size || 0) / 1024 / 1024 >
      defaultAgentKnowledge,
    [defaultAgentKnowledge, knowledgeInfo?.info.total_size]
  );

  useEffect(() => {
    if (
      trainingStatus &&
      trainingStatus.status !== "in_process" &&
      untrainedKnowledgeIds?.length &&
      isTotalSizeExceeded &&
      !isAgentRetrainedPopupOpen
    ) {
      setIsKnowledgeLimitPopupOpen(true);
    }
  }, [
    isAgentRetrainedPopupOpen,
    isTotalSizeExceeded,
    trainingStatus,
    untrainedKnowledgeIds?.length,
  ]);

  useEffect(() => {
    if (
      trainingRunInProgressRef.current &&
      trainingStatus?.status === "completed"
    ) {
      trainingRunInProgressRef.current = false;
      if (hasAnyKnowledge) {
        setWasFirstTraining(!knowledgeInfo?.info.last_updated);
        handleRefreshKnowledge();
        knowledgeInfoRefetch();
        setIsKnowledgeLimitPopupOpen(false);
        setIsAgentRetrainedPopupOpen(true);
      }
    }
  }, [
    handleRefreshKnowledge,
    hasAnyKnowledge,
    knowledgeInfo?.info.last_updated,
    knowledgeInfoRefetch,
    trainingStatus,
  ]);

  const value: KnowledgeTrainingContextValue = useMemo(
    () => ({
      trainingStatus: trainingStatus?.status,
      isTrainingStatusLoading,
      isTrainingStatusError,
      isTrainingStatusFetching,
      trainingStatusRefetch,
      isTotalSizeExceeded,
      trainAgent: handleTrainAgent,
      isTrainingLoading,
    }),
    [
      trainingStatus?.status,
      isTrainingStatusLoading,
      isTrainingStatusError,
      isTrainingStatusFetching,
      trainingStatusRefetch,
      isTotalSizeExceeded,
      handleTrainAgent,
      isTrainingLoading,
    ]
  );

  const knowledgeLimitHighlight = (text: ReactNode) => (
    <span className="bg-linear-to-r from-[#ADC7FF] to-[#CD9AFF] bg-clip-text font-semibold text-transparent">
      {text}
    </span>
  );

  const isFirstTraining = !knowledgeInfo?.info.last_updated;

  return (
    <KnowledgeTrainingContext.Provider value={value}>
      {children}
      <CXKnowledgeTrainingPopup
        title={
          wasFirstTraining
            ? t({
                id: "cx-ai-agents-page.agent-trained-popup-title",
                defaultMessage: "Your Agent is Trained",
              })
            : t({
                id: "cx-ai-agents-page.agent-retrained-popup-title",
                defaultMessage: "Your Agent is Retrained",
              })
        }
        subtitle={
          wasFirstTraining
            ? t({
                id: "cx-ai-agents-page.agent-trained-popup-subtitle",
                defaultMessage:
                  "Training of Agent based on knowledge is completed! 🙌",
              })
            : t({
                id: "cx-ai-agents-page.agent-retrained-popup-subtitle",
                defaultMessage:
                  "Retraining of Agent based on updated knowledge is completed! 🙌",
              })
        }
        isOpen={isAgentRetrainedPopupOpen}
        button={{
          text: t({ id: "common.got-it", defaultMessage: "Got it" }),
          onClick: () => setIsAgentRetrainedPopupOpen(false),
        }}
        img={
          <img
            src={ghostImage}
            alt=""
            className="pointer-events-none relative mt-[14px] h-[60px]"
          />
        }
      />
      <CXKnowledgeTrainingPopup
        title={t({
          id: "cx-ai-agents-page.knowledge-limit-popup-title",
          defaultMessage: "Knowledge limit is reached",
        })}
        img={
          <Lottie
            options={{ animationData: headAnimation }}
            width={78}
            height={78}
          />
        }
        subtitle={
          isFirstTraining
            ? t(
                {
                  id: "cx-ai-agents-page.knowledge-limit-popup-subtitle-training",
                  defaultMessage:
                    "Training of the Agent is not possible as knowledge limit is exceeded. <highlightedText>Manage Knowledge</highlightedText> to train Agent",
                },
                { highlightedText: knowledgeLimitHighlight }
              )
            : t(
                {
                  id: "cx-ai-agents-page.knowledge-limit-popup-subtitle",
                  defaultMessage:
                    "Retraining of the Agent is not possible as knowledge limit is exceeded. <highlightedText>Manage Knowledge</highlightedText> to retrain Agent",
                },
                { highlightedText: knowledgeLimitHighlight }
              )
        }
        isOpen={isKnowledgeLimitPopupOpen}
        button={{
          text: t({ id: "common.got-it", defaultMessage: "Got it" }),
          onClick: () => setIsKnowledgeLimitPopupOpen(false),
        }}
      />
    </KnowledgeTrainingContext.Provider>
  );
};
